import { likedFolderService, likeGoodsService } from '../../../api';
import { bridge, delay, isIOS, isWeb } from '../../../utils';
import { isAndroid } from '../../../utils/isAndroid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Dimensions, Keyboard, TextInput } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { queryKey } from '../../../constant';

import {
  BottomSheetContainer,
  ColorValues,
  createUseBottomSheetModal,
  Flex,
  KeyboardAvoiding,
  PrimaryButton,
  Typography,
} from '@banhala/ui-kit';
import Toast from 'react-native-toast-message';

type UseCreateNewLikeFolderBottomSheet = {
  goodsSno: number;
  listParams?: string;
  screen: string;
};
export const useCreateNewLikeFolderBottomSheet = createUseBottomSheetModal<
  { goodsSno: number; folderName: string; folderSno: number },
  UseCreateNewLikeFolderBottomSheet
>(({ confirm, cancel, param: { goodsSno, listParams } }) => {
  const { bottom, top } = useSafeAreaInsets();
  const [title, setTitle] = useState('');
  const ref = useRef<TextInput>(null);
  const bottomSheetMinHeightInNative =
    (Dimensions.get('window').height - bottom - top) / 3;
  const bottomSheetMinHeightInWeb = useRef(Dimensions.get('window').height / 3);

  const { mutateAsync: mutateAsyncCreateLikeFolder } = useMutation({
    mutationFn: likedFolderService.post,
    throwOnError: false,
  });
  const { mutateAsync: mutateAsyncLikeGoods } = useMutation({
    mutationFn: likeGoodsService.post,
    throwOnError: false,
  });

  const queryClient = useQueryClient();

  return (
    <KeyboardAvoiding>
      <BottomSheetContainer
        onPressClose={() => {
          Keyboard.dismiss();
          cancel();
        }}
        minHeight={
          isWeb
            ? bottomSheetMinHeightInWeb.current
            : isIOS
              ? bottomSheetMinHeightInNative
              : 0
        }
        title="새 서랍 만들기"
      >
        <Flex.Column
          position={'relative'}
          flex={isAndroid ? undefined : 1}
          padding={{ vertical: 16 }}
        >
          <Flex.Column
            padding={{ horizontal: 10 }}
            height={isAndroid ? 78 : undefined}
          >
            <TextInput
              ref={ref}
              style={{
                fontSize: 16,
                outlineWidth: 0,
              }}
              placeholder="서랍 이름은 최소 한 글자 이상 입력해주세요."
              placeholderTextColor={ColorValues.gray50}
              autoFocus={!isWeb}
              multiline
              numberOfLines={2}
              onChangeText={value => {
                setTitle(value);
              }}
              maxLength={30}
            />
            <Flex.Row position={'absolute'} right={10} top={60}>
              <Typography
                color={'content_secondary'}
              >{`${title.length}`}</Typography>
              <Typography color={'content_disabled'}>{` / 30`}</Typography>
            </Flex.Row>
          </Flex.Column>
        </Flex.Column>
        <PrimaryButton
          variant={'fill'}
          color={'pink'}
          removeBorderRadius={true}
          // disabled={title.length === 0}
          state={title.length === 0 ? 'disabled' : undefined}
          onPress={async () => {
            try {
              const {
                data: { sno },
              } = await mutateAsyncCreateLikeFolder({
                title,
              });
              await mutateAsyncLikeGoods({
                list_params_referrer: listParams,
                sno: goodsSno,
                folder_sno: sno,
              });
              // toast는 호출자(useAddLikeGoods.selectFolder)가 모달 dismiss 완료 후 발화한다.
              // 모달 내부에서 toast를 띄우면 Android에서 dismiss와 race로 묻히는 이슈가 있어 위치를 옮김.
              // (기존 서랍 선택 경로의 fix 패턴 — 83d4f0d3e9 + b2d7123bdb — 과 동일하게 통일)
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteLikeFolders,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.getInfiniteRecentlyViewedGoods,
              });
              queryClient.invalidateQueries({
                queryKey: queryKey.myInfo,
              });

              bridge.likeGoods({
                goods_sno: goodsSno,
                is_liked: true,
                folder_sno: sno,
              });
              confirm({ goodsSno, folderName: title, folderSno: sno });
            } catch (error) {
              // iOS: react-native-toast-message가 avoidKeyboard=true 기본값으로 키보드 높이만큼
              // 토스트를 위로 올리는 동작이 있어, TextInput 포커스가 살아있는 상태로 Toast.show 시
              // 화면 중앙에 노출됨. onPressClose 패턴과 동일하게 명시적 dismiss 처리.
              Keyboard.dismiss();
              cancel();
              await delay(500);
              const errorMessage =
                error instanceof Error && error.message
                  ? error.message
                  : '오류가 발생했어요';
              Toast.show({
                type: 'success',
                text1: errorMessage,
                position: 'bottom',
              });
            }
          }}
        >
          완료
        </PrimaryButton>
      </BottomSheetContainer>
    </KeyboardAvoiding>
  );
});

